import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  InvalidFormField,
  ShopPageNotFound,
  StoreNotFound,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { type NearbyQuery, PublicShopPage, PublicShopSummary, ShopsNearby } from "../model/shop";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

/** `/shops`: how a customer finds a shop, from the map, a link, or the QR code they scanned. */
export class ShopsApi {
  constructor(private readonly t: Transport) {}

  /** Shops around a coordinate, widening the radius until it finds a few. */
  async nearby(ctx: ApiContext, query: NearbyQuery): Promise<Result<ShopsNearby, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.get(ctx, "/shops/nearby", { query: { ...query } });
    return forward(
      r,
      (j) => parse(ShopsNearby, j),
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  /** The hosted shop page by handle. No session needed: `anonymous` works. */
  async page(ctx: ApiContext, slug: string): Promise<Result<PublicShopPage, ApiNotAuthorized | ShopPageNotFound>> {
    const r = await this.t.get(ctx, `/shops/page/${slug}`);
    return forward(
      r,
      (j) => parse(PublicShopPage, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "") return new ShopPageNotFound(slug, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** A shop's name by store id: what a QR arrival shows in the header. A suspended shop is not found. */
  async summary(
    ctx: ApiContext,
    storeId: string,
  ): Promise<Result<PublicShopSummary, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/shops/${storeId}`);
    return forward(
      r,
      (j) => parse(PublicShopSummary, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
